import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ProjectStatsQueryDTO } from "@/modules/management/project/domain/repo/get-project-stats";
import type { ProjectLevel } from "@/modules/management/project/domain/types";
import { formatDateForInput } from "@/shared/lib/date-time";
import { Card, cn } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Activity,
  AlertTriangle,
  Building2,
  CalendarRange,
  CheckCircle2,
  CircleDot,
  Clock,
  FolderOpen,
  Layers,
  ListTodo,
  Network,
  PauseCircle,
  Percent,
  PlayCircle,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useProjectStats } from "../api/queries";
import { ProjectSummaryFilter } from "../ui/ProjectSummaryFilter";
import { ProjectSummaryStatPopoverCard } from "../ui/ProjectSummaryStatPopoverCard";

const SUMMARY_LEVEL_ALL = "__all__";

function parseSummaryLevel(raw: string | undefined): string {
  if (raw === "organization" || raw === "department" || raw === "position") {
    return raw;
  }
  return SUMMARY_LEVEL_ALL;
}

function levelLabel(level: string) {
  switch (level) {
    case "organization":
      return "ລະດັບອົງກອນ";
    case "department":
      return "ລະດັບຜະແນກ";
    case "position":
      return "ລະດັບຕຳແໜ່ງ";
    default:
      return level;
  }
}

function levelChipClass(level: string) {
  switch (level) {
    case "organization":
      return "border-primary/40 bg-primary/5 text-foreground";
    case "department":
      return "border-sky-500/40 bg-sky-500/5 text-foreground dark:border-sky-500/30 dark:bg-sky-950/30";
    case "position":
      return "border-amber-500/40 bg-amber-500/5 text-foreground dark:border-amber-500/30 dark:bg-amber-950/30";
    default:
      return "border-border bg-muted/40";
  }
}

export function ProjectSummaryPage() {
  const nav = useNavigate();
  const searchParams = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >;

  const [departmentId, setDepartmentId] = useState(
    searchParams.departmentId ?? "",
  );
  const [level, setLevel] = useState(() =>
    parseSummaryLevel(searchParams.level),
  );
  const [startDateFrom, setStartDateFrom] = useState(
    searchParams.startDateFrom ??
      formatDateForInput(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)),
  );
  const [startDateTo, setStartDateTo] = useState(
    searchParams.startDateTo ?? formatDateForInput(new Date()),
  );

  const statsQuery: ProjectStatsQueryDTO = useMemo(
    () => ({
      departmentId: departmentId || undefined,
      startDateFrom: startDateFrom || undefined,
      startDateTo: startDateTo || undefined,
      level: level !== SUMMARY_LEVEL_ALL ? (level as ProjectLevel) : undefined,
    }),
    [departmentId, startDateFrom, startDateTo, level],
  );

  const { data: stats, isLoading } = useProjectStats(statsQuery);

  const applyFilters = () => {
    nav({
      to: "/app/management/projects/summary",
      search: {
        departmentId: departmentId || undefined,
        startDateFrom: startDateFrom || undefined,
        startDateTo: startDateTo || undefined,
        level:
          level !== SUMMARY_LEVEL_ALL ? (level as ProjectLevel) : undefined,
      },
    });
  };

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-6">
          <div>
            <h1 className="font-bold text-2xl">ສະຫຼຸບຜົນໂຄງການ</h1>
            <p className="mt-1 text-muted-foreground">
              ສະຖິຕິໂຄງການ, ວຽກຍ່ອຍ ແລະ ຄວາມສຽງ ຕາມຊ່ວງວັນເລີ່ມ ແລະ ພະແນກ
            </p>
          </div>

          <ProjectSummaryFilter
            departmentId={departmentId}
            level={level}
            startDateFrom={startDateFrom}
            startDateTo={startDateTo}
            onDepartmentChange={setDepartmentId}
            onLevelChange={setLevel}
            onStartDateFromChange={setStartDateFrom}
            onStartDateToChange={setStartDateTo}
            onApply={applyFilters}
          />

          {isLoading ? (
            <div className="flex min-h-[320px] items-center justify-center">
              <div className="text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
                <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
              </div>
            </div>
          ) : stats ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <ProjectSummaryStatPopoverCard
                  statsQuery={statsQuery}
                  label="ທັງໝົດ"
                  value={stats.overall.total}
                  icon={<Layers className="h-6 w-6 text-primary" />}
                  iconWrapClassName="rounded-full bg-primary/10 p-3"
                  cardClassName="border border-border bg-card p-4"
                />
                <ProjectSummaryStatPopoverCard
                  statsQuery={statsQuery}
                  status="active"
                  label="ດຳເນີນຢູ່"
                  value={stats.overall.active}
                  labelClassName="text-green-700 text-sm dark:text-green-400"
                  valueClassName="font-bold text-2xl text-green-700 dark:text-green-400"
                  icon={
                    <Activity className="h-6 w-6 text-green-600 dark:text-green-400" />
                  }
                  iconWrapClassName="rounded-full bg-green-500/15 p-3 dark:bg-green-500/20"
                  cardClassName="border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/20"
                />
                <ProjectSummaryStatPopoverCard
                  statsQuery={statsQuery}
                  status="completed"
                  label="ສຳເລັດ"
                  value={stats.overall.completed}
                  labelClassName="text-blue-700 text-sm dark:text-blue-400"
                  valueClassName="font-bold text-2xl text-blue-700 dark:text-blue-400"
                  icon={
                    <CheckCircle2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  }
                  iconWrapClassName="rounded-full bg-blue-500/15 p-3 dark:bg-blue-500/20"
                  cardClassName="border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/20"
                />
                <ProjectSummaryStatPopoverCard
                  statsQuery={statsQuery}
                  status="on_hold"
                  label="ຢຸດຊົ່ວຄາວ"
                  value={stats.overall.onHold}
                  labelClassName="text-amber-800 text-sm dark:text-amber-400"
                  valueClassName="font-bold text-2xl text-amber-800 dark:text-amber-400"
                  icon={
                    <PauseCircle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  }
                  iconWrapClassName="rounded-full bg-amber-500/15 p-3 dark:bg-amber-500/20"
                  cardClassName="border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/20"
                />
                <Card className="flex flex-col gap-0 border border-violet-200 bg-violet-50 p-4 dark:border-violet-800 dark:bg-violet-950/20">
                  <div className="flex items-center justify-between gap-0">
                    <div className="flex min-w-0 flex-col gap-0">
                      <p className="text-sm text-violet-800 dark:text-violet-400">
                        ຄວາມຄືບໜ້າສະເລ່ຍ (ລວມ)
                      </p>
                      <p className="font-bold text-2xl text-violet-800 dark:text-violet-400">
                        {stats.overall.avgProgress}%
                      </p>
                    </div>
                    <div className="shrink-0 rounded-full bg-violet-500/15 p-3 dark:bg-violet-500/20">
                      <Percent className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                    </div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card className="gap-0 overflow-hidden border-border/60 py-0 shadow-sm">
                  <div className="flex items-center gap-3 border-border/50 border-b bg-muted/40 px-4 py-3">
                    <div className="rounded-xl bg-primary/10 p-2.5">
                      <ListTodo className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="font-semibold text-base leading-snug">
                      ວຽກຍ່ອຍ (ທັງໝົດໃນຊ່ວງທີ່ກອງ)
                    </h2>
                  </div>
                  <div className="divide-y divide-border/60 px-4 py-1 text-sm">
                    <div className="flex items-center justify-between gap-3 py-3">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4 shrink-0 text-amber-500" />
                        ລໍຖ້າ
                      </span>
                      <span className="font-semibold tabular-nums">
                        {stats.tasks.pending}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 py-3">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <PlayCircle className="h-4 w-4 shrink-0 text-sky-500" />
                        ກຳລັງເຮັດ
                      </span>
                      <span className="font-semibold tabular-nums">
                        {stats.tasks.inProgress}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 py-3">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 dark:text-green-500" />
                        ສຳເລັດ
                      </span>
                      <span className="font-semibold text-green-700 tabular-nums dark:text-green-400">
                        {stats.tasks.done}
                      </span>
                    </div>
                  </div>
                </Card>

                <Card className="gap-0 overflow-hidden border-border/60 py-0 shadow-sm">
                  <div className="flex items-center gap-3 border-border/50 border-b bg-muted/40 px-4 py-3">
                    <div className="rounded-xl bg-rose-500/10 p-2.5 dark:bg-rose-950/40">
                      <ShieldAlert className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                    </div>
                    <h2 className="font-semibold text-base leading-snug">
                      ຄວາມສຽງ (ທັງໝົດໃນຊ່ວງທີ່ກອງ)
                    </h2>
                  </div>
                  <div className="divide-y divide-border/60 px-4 py-1 text-sm">
                    <div className="flex items-center justify-between gap-3 py-3">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <CircleDot className="h-4 w-4 shrink-0 text-orange-500" />
                        ເປີດ
                      </span>
                      <span className="font-semibold tabular-nums">
                        {stats.risks.open}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 py-3">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <ShieldCheck className="h-4 w-4 shrink-0 text-blue-500" />
                        ບັນທຶກການລົດສຽງ
                      </span>
                      <span className="font-semibold tabular-nums">
                        {stats.risks.mitigated}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 py-3">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                        ປິດ
                      </span>
                      <span className="font-semibold tabular-nums">
                        {stats.risks.closed}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 bg-destructive/5 py-3 last:border-b-0 dark:bg-destructive/10">
                      <span className="flex items-center gap-2 font-medium text-destructive">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        ເປີດ + ລະດັບສູງ
                      </span>
                      <span className="font-semibold text-destructive tabular-nums">
                        {stats.risks.highOpen}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>

              <Card className="gap-0 overflow-hidden border-border/60 py-0 shadow-sm">
                <div className="flex items-center gap-3 border-border/50 border-b bg-muted/40 px-4 py-3">
                  <div className="rounded-xl bg-primary/10 p-2.5">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <h2 className="font-semibold text-base">ຕາມພະແນກ</h2>
                </div>
                {stats.byDepartment.length === 0 ? (
                  <p className="px-4 py-8 text-center text-muted-foreground text-sm">
                    ບໍ່ມີຂໍ້ມູນ
                  </p>
                ) : (
                  <ul className="divide-y divide-border/60 px-2 py-1">
                    {stats.byDepartment.map((row) => (
                      <li
                        key={row.departmentId ?? "none"}
                        className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 rounded-lg px-2 py-3 text-sm transition-colors hover:bg-muted/50"
                      >
                        <span className="min-w-0 font-medium">
                          {row.departmentName || "ບໍ່ລະບຸພະແນກ"}
                        </span>
                        <span className="max-w-full text-right text-muted-foreground text-xs leading-relaxed sm:text-sm">
                          ທັງໝົດ {row.total} · ດຳເນີນ {row.active} · ສຳເລັດ{" "}
                          {row.completed} · ຢຸດ {row.onHold}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card className="gap-0 overflow-hidden border-border/60 py-0 shadow-sm">
                <div className="flex items-center gap-3 border-border/50 border-b bg-muted/40 px-4 py-3">
                  <div className="rounded-xl bg-violet-500/10 p-2.5 dark:bg-violet-950/40">
                    <Network className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <h2 className="font-semibold text-base">ຕາມລະດັບໂຄງການ</h2>
                </div>
                <div className="flex flex-wrap gap-2 p-4">
                  {stats.byLevel.map((row) => (
                    <div
                      key={row.level}
                      className={cn(
                        "flex min-w-[10rem] flex-1 items-baseline justify-between gap-3 rounded-xl border px-4 py-3 text-sm shadow-sm",
                        levelChipClass(row.level),
                      )}
                    >
                      <span className="font-semibold">
                        {levelLabel(row.level)}
                      </span>
                      <span className="text-muted-foreground tabular-nums">
                        {row.total}{" "}
                        <span className="text-foreground/80">ໂຄງການ</span>
                      </span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="gap-0 overflow-hidden border-border/60 py-0 shadow-sm">
                <div className="flex items-center gap-3 border-border/50 border-b bg-muted/40 px-4 py-3">
                  <div className="rounded-xl bg-sky-500/10 p-2.5 dark:bg-sky-950/40">
                    <CalendarRange className="h-5 w-5 text-sky-600 dark:text-sky-400" />
                  </div>
                  <h2 className="font-semibold text-base leading-snug">
                    ແນວໂນ້ມຕາມເດືອນ (ວັນເລີ່ມໂຄງການ)
                  </h2>
                </div>
                {stats.byStartMonth.length === 0 ? (
                  <p className="px-4 py-8 text-center text-muted-foreground text-sm">
                    ບໍ່ມີໂຄງການທີ່ມີວັນເລີ່ມໃນຊ່ວງທີ່ເລືອກ
                  </p>
                ) : (
                  <div className="divide-y divide-border/60 px-4 py-1">
                    {stats.byStartMonth.map((m) => (
                      <div
                        key={m.month}
                        className="flex flex-col gap-1 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                      >
                        <span className="shrink-0 font-semibold tabular-nums">
                          {m.month}
                        </span>
                        <span className="text-muted-foreground text-xs leading-relaxed sm:text-right sm:text-sm">
                          ທັງໝົດ {m.total} · ດຳເນີນ {m.active} · ສຳເລັດ{" "}
                          {m.completed}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {stats.overall.total === 0 && (
                <Card className="border-muted-foreground/25 border-dashed bg-muted/20 px-6 py-10 text-center shadow-none">
                  <div className="mx-auto flex max-w-sm flex-col items-center gap-3">
                    <div className="rounded-full bg-muted p-3">
                      <FolderOpen className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      ບໍ່ພົບໂຄງການຕາມເງື່ອນໄຂ
                    </p>
                  </div>
                </Card>
              )}
            </>
          ) : null}
        </div>
      </Main>
    </>
  );
}
